import React, { useState, useEffect, useRef, useCallback } from "react";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { projectService } from "@/services/projectService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface ProjectInputProps {
  value: string | null;
  clientId?: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  optionsSource?: LookupInputOption[];
  placeholder?: string;
  onChange?: (projectId: string | null) => void;
  showDeposit?: boolean;
}

export function ProjectInput({
  value,
  clientId,
  state = "editing",
  label = "Project",
  error,
  optionsSource,
  placeholder = "Select project\u2026",
  onChange,
  showDeposit,
}: ProjectInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleIdlePress = useCallback(() => {
    if (!value) return;
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
    if (currentPanel && currentId) {
      pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
    }
    navigateToEntity("project", value);
  }, [value, currentParams, pushNavigation, currentEntity, navigateToEntity]);
  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [loading, setLoading] = useState(false);
  const prevClientId = useRef(clientId);

  // Auto-clear value when clientId changes
  useEffect(() => {
    if (prevClientId.current !== clientId && prevClientId.current !== undefined) {
      onChange?.(null);
    }
    prevClientId.current = clientId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    // When an external options source is provided, use it directly
    if (optionsSource) {
      setOptions(optionsSource);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const projects = clientId
          ? await projectService.getProjectsByClientId(clientId)
          : await projectService.getProjects();
        if (cancelled) return;
        setOptions(
          projects.map((p) => {
            const clientName = (p as any).client?.name;
            const depositStr = showDeposit
              ? typeof p.meta_data?.deposit_amount === "number" && !isNaN(p.meta_data.deposit_amount)
                ? `Deposit: $${p.meta_data.deposit_amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "Deposit: —"
              : undefined;
            const subtitle = [clientName, depositStr].filter(Boolean).join(" · ") || undefined;
            return {
              id: p.id!,
              name: p.name,
              subtitle,
              highlightSub: true,
              icon: "folder" as const,
              badge: p.status
                ? {
                    text: p.status,
                    tone: p.status === "IN PROGRESS" ? ("emerald" as const) : ("amber" as const),
                  }
                : undefined,
            };
          }),
        );
      } catch (err) {
        console.error("Failed to load projects:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clientId, showDeposit, optionsSource]);

  const resolvedState: LookupInputState = loading ? "loading" : state;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="folder"
      error={error}
      placeholder={placeholder}
      onChange={onChange}
      onIdlePress={handleIdlePress}
    />
  );
}
